import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * useAutoRefresh — reusable background-refresh hook.
 *
 * Calls `fetchFn` every `intervalMs` (default 60 s) with:
 *  • Visibility-aware pause: no polling while the tab is hidden. When the
 *    tab becomes visible again we refresh immediately, then resume the
 *    interval. Saves bandwidth + battery.
 *  • React-safe cleanup: cancels the interval + skips setState after unmount.
 *  • Debounced first fire: the interval starts AFTER the initial `intervalMs`
 *    (the parent component's own `useEffect` should still do the first fetch
 *    at mount so the UI paints without waiting).
 *
 * @param {Function} fetchFn  async () => void — user's refresh function.
 * @param {number}   intervalMs  default 60 000.
 * @param {boolean}  enabled  when false, the interval is not scheduled.
 * @returns {{lastRefreshedAt: number | null, isRefreshing: boolean, refresh: Function}}
 */
export default function useAutoRefresh(fetchFn, intervalMs = 60000, enabled = true) {
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const fnRef = useRef(fetchFn);
  const mountedRef = useRef(true);

  // Keep the latest fetchFn reference without re-scheduling the interval.
  useEffect(() => { fnRef.current = fetchFn; }, [fetchFn]);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const refresh = useCallback(async () => {
    if (!mountedRef.current || document.hidden) return;
    setIsRefreshing(true);
    try {
      await fnRef.current();
      if (mountedRef.current) setLastRefreshedAt(Date.now());
    } catch (_e) {
      // fetchFn is expected to handle its own errors. Swallow here so a
      // transient network blip doesn't kill the timer.
    } finally {
      if (mountedRef.current) setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    const interval = setInterval(() => { refresh(); }, intervalMs);
    const onVisible = () => { if (!document.hidden) refresh(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [enabled, intervalMs, refresh]);

  return { lastRefreshedAt, isRefreshing, refresh };
}
